import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface Props {
    /** 0-1 within the container. */
    x: number;
    y: number;
    container: Element | null;
    className: string;
    children: React.ReactNode;
}

const VIEW_PAD = 8;

/** Keep the already-transformed tip inside the viewport. */
export function clampTipShift(
    rect: Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>,
    vw: number,
    vh: number,
    pad = VIEW_PAD
): { dx: number; dy: number } {
    let dx = 0;
    let dy = 0;
    if (rect.right > vw - pad) dx -= rect.right - (vw - pad);
    if (rect.left + dx < pad) dx += pad - (rect.left + dx);
    if (rect.bottom > vh - pad) dy -= rect.bottom - (vh - pad);
    if (rect.top + dy < pad) dy += pad - (rect.top + dy);
    return { dx, dy };
}

/** Hover bubble on document.body so map/chart/panel overflow cannot clip it. */
const InsightsFixedTip: React.FC<Props> = ({ x, y, container, className, children }) => {
    const [box, setBox] = useState<DOMRect | null>(null);
    const tipRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!container) {
            setBox(null);
            return undefined;
        }
        const update = (): void => setBox(container.getBoundingClientRect());
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [container, x, y]);

    const left = box ? box.left + x * box.width : 0;
    const top = box ? box.top + y * box.height : 0;
    const above = y > 0.38 || top > (typeof window === 'undefined' ? 0 : window.innerHeight) - 260;
    const tipX =
        x > 0.72 || left > (typeof window === 'undefined' ? 0 : window.innerWidth) - 180
            ? '-100%'
            : x < 0.28 || left < 180
            ? '0'
            : '-50%';
    const baseTransform = `translate(${tipX}, ${above ? 'calc(-100% - 12px)' : '14px'})`;

    useLayoutEffect(() => {
        const el = tipRef.current;
        if (!el || !box) return undefined;
        el.style.transform = baseTransform;
        const { dx, dy } = clampTipShift(
            el.getBoundingClientRect(),
            window.innerWidth,
            window.innerHeight
        );
        if (dx || dy) el.style.transform = `${baseTransform} translate(${dx}px, ${dy}px)`;
        return undefined;
    }, [box, baseTransform, children]);

    if (!box) return null;

    return ReactDOM.createPortal(
        <div
            ref={tipRef}
            className={className}
            style={{
                position: 'fixed',
                left,
                top,
                transform: baseTransform,
                zIndex: 200,
            }}
        >
            {children}
        </div>,
        document.body
    );
};

export default InsightsFixedTip;
